class BwsPdpSectionNavigation extends HTMLElement {
  #controller;
  #frame;
  #links = [];
  #sections = [];
  #breakoutSections = [];
  #fadeTargets = [];
  #scrollRoot;
  #shell;
  #activeNumber;
  #desktopQuery;
  #reduceMotionQuery;
  #resizeObserver;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#desktopQuery = window.matchMedia('(min-width: 990px)');
    this.#reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#refresh();

    const { signal } = this.#controller;
    document.addEventListener('scroll', this.#requestUpdate, { capture: true, passive: true, signal });
    this.addEventListener('click', this.#handleLinkClick, { signal });
    this.addEventListener('pointerenter', this.#handlePointerEnter, { signal });
    this.addEventListener('pointerleave', this.#handlePointerLeave, { signal });
    window.addEventListener('resize', this.#requestUpdate, { passive: true, signal });
    this.#desktopQuery.addEventListener('change', this.#refresh, { signal });
    this.#reduceMotionQuery.addEventListener('change', this.#requestUpdate, { signal });
    document.addEventListener('shopify:section:load', this.#refresh, { signal });
    document.addEventListener('shopify:section:reorder', this.#refresh, { signal });

    this.#resizeObserver = new ResizeObserver(this.#requestUpdate);
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();

    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  #refresh = () => {
    this.#scrollRoot = this.closest('.content-for-layout');
    this.#shell = this.closest('.bws-pdp-shell');
    this.#links = [...this.querySelectorAll('[data-bws-pdp-section-link]')];
    this.#sections = this.#scrollRoot
      ? [...this.#scrollRoot.querySelectorAll('[data-bws-pdp-section]')]
      : [...document.querySelectorAll('[data-bws-pdp-section]')];
    this.#breakoutSections = this.#scrollRoot
      ? [...this.#scrollRoot.querySelectorAll('[data-bws-pdp-breakout]')]
      : [...document.querySelectorAll('[data-bws-pdp-breakout]')];
    this.#fadeTargets = this.#scrollRoot
      ? [...this.#scrollRoot.querySelectorAll('[data-bws-nav-fade]')]
      : [];
    this.#syncExpandedHeight();
    this.#update();
  };

  #requestUpdate = () => {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      this.#update();
    });
  };

  #handleLinkClick = (event) => {
    const link = event.target.closest('[data-bws-pdp-section-link]');
    if (!link || !this.contains(link)) return;

    const targetId = link.hash.slice(1);
    const target = targetId ? document.getElementById(targetId) : null;
    if (!target) return;

    event.preventDefault();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });

    if (window.location.hash !== link.hash) history.pushState(null, '', link.hash);
  };

  #handlePointerEnter = () => {
    if (!this.#desktopQuery?.matches || this.classList.contains('bws-sticky-atc__mobile-navigation')) return;

    this.setAttribute('data-pointer-open', '');
  };

  #handlePointerLeave = () => {
    this.removeAttribute('data-pointer-open');
  };

  #update() {
    if (this.#sections.length === 0) return;

    const scrollRootBounds = this.#getScrollBounds();
    this.#updateBreakoutProgress(scrollRootBounds);
    this.#updateCoverFade();
    const activationLine = scrollRootBounds
      ? scrollRootBounds.top + scrollRootBounds.height * 0.35
      : window.innerHeight * 0.35;
    const activeSection =
      this.#sections.find((section) => {
        const bounds = section.getBoundingClientRect();
        return bounds.top <= activationLine && bounds.bottom > activationLine;
      }) ??
      this.#sections.reduce((closest, section) => {
        const distance = Math.abs(section.getBoundingClientRect().top - activationLine);
        return distance < closest.distance ? { section, distance } : closest;
      }, { section: this.#sections[0], distance: Number.POSITIVE_INFINITY }).section;
    const activeNumber = activeSection.dataset.bwsPdpSection;

    for (const link of this.#links) {
      const isActive = link.dataset.bwsPdpSectionLink === activeNumber;
      link.toggleAttribute('aria-current', isActive);
      if (isActive) link.setAttribute('aria-current', 'location');
    }

    this.#updateDesktopNavigationState(activeNumber);

    if (activeNumber !== this.#activeNumber) {
      this.#activeNumber = activeNumber;
      const activeLink = this.#links.find((link) => link.dataset.bwsPdpSectionLink === activeNumber);

      if (activeLink && this.classList.contains('bws-sticky-atc__mobile-navigation')) {
        const targetLeft = activeLink.offsetLeft - (this.clientWidth - activeLink.offsetWidth) / 2;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.scrollTo({ left: targetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
      }
    }
  }

  #syncExpandedHeight() {
    if (!this.#desktopQuery?.matches || this.classList.contains('bws-sticky-atc__mobile-navigation')) {
      this.removeAttribute('data-nav-ready');
      this.style.removeProperty('--bws-pdp-nav-expanded-height');
      return;
    }

    const list = this.querySelector('.bws-pdp-shell__section-navigation-list');
    if (!list) return;

    this.style.setProperty('--bws-pdp-nav-expanded-height', `${list.offsetTop + list.scrollHeight}px`);
    this.setAttribute('data-nav-ready', '');
  }

  #updateDesktopNavigationState(activeNumber) {
    const isDesktopNavigation =
      this.#desktopQuery?.matches && !this.classList.contains('bws-sticky-atc__mobile-navigation');

    if (!isDesktopNavigation) {
      this.removeAttribute('data-collapsed');
      this.removeAttribute('data-pointer-open');
      this.style.removeProperty('--bws-pdp-nav-active-offset');
      return;
    }

    const activeLink = this.#links.find((link) => link.dataset.bwsPdpSectionLink === activeNumber);
    const activeItem = activeLink?.closest('li');
    this.style.setProperty('--bws-pdp-nav-active-offset', `${-(activeItem?.offsetTop ?? 0)}px`);
    this.toggleAttribute('data-collapsed', this.#getScrollTop() > 64);
  }

  #getScrollTop() {
    if (!this.#scrollRoot) return window.scrollY;

    const styles = getComputedStyle(this.#scrollRoot);
    const usesElementScroll =
      /(auto|scroll)/.test(styles.overflowY) &&
      this.#scrollRoot.scrollHeight > this.#scrollRoot.clientHeight + 1;

    return usesElementScroll ? this.#scrollRoot.scrollTop : window.scrollY;
  }

  #getScrollBounds() {
    if (!this.#scrollRoot) return null;

    const styles = getComputedStyle(this.#scrollRoot);
    const usesElementScroll =
      /(auto|scroll)/.test(styles.overflowY) &&
      this.#scrollRoot.scrollHeight > this.#scrollRoot.clientHeight + 1;

    if (usesElementScroll) return this.#scrollRoot.getBoundingClientRect();

    return { top: 0, height: window.innerHeight };
  }

  #updateBreakoutProgress(scrollRootBounds) {
    if (this.#breakoutSections.length === 0 || !this.#shell) return;

    const rootTop = scrollRootBounds?.top ?? 0;
    const rootHeight = scrollRootBounds?.height ?? window.innerHeight;
    const rootBottom = rootTop + rootHeight;
    const transitionDistance = Math.max(rootHeight * 0.18, 1);
    const breakoutProgress = this.#breakoutSections.reduce((highestProgress, breakoutSection) => {
      const sectionBounds = breakoutSection.getBoundingClientRect();
      const enteringProgress = Math.min(Math.max((rootBottom - sectionBounds.top) / transitionDistance, 0), 1);
      const leavingProgress = Math.min(Math.max((sectionBounds.bottom - rootTop) / transitionDistance, 0), 1);

      return Math.max(highestProgress, Math.min(enteringProgress, leavingProgress));
    }, 0);

    this.#shell.style.setProperty('--bws-pdp-breakout-progress', breakoutProgress.toFixed(3));
  }

  #updateCoverFade() {
    if (this.classList.contains('bws-sticky-atc__mobile-navigation')) return;
    if (!this.#desktopQuery?.matches || this.#fadeTargets.length === 0) return;

    const host = this.getBoundingClientRect();
    const before = getComputedStyle(this, '::before');
    const fade = this.#reduceMotionQuery?.matches ? 0 : 48;
    const insetStart = Math.abs(parseFloat(before.insetInlineStart) || 32);
    const insetEnd = Math.abs(parseFloat(before.insetInlineEnd) || 24);
    const cover = {
      left: host.left - insetStart,
      top: host.top,
      width: host.width + insetStart + insetEnd,
      height: host.height + fade,
    };

    if (this.#scrollRoot) {
      this.#scrollRoot.style.setProperty('--bws-nav-cover-fade', `${fade}px`);
    }

    for (const target of this.#fadeTargets) {
      const bounds = target.getBoundingClientRect();
      target.style.setProperty('--bws-nav-cover-x', `${cover.left - bounds.left}px`);
      target.style.setProperty('--bws-nav-cover-y', `${cover.top - bounds.top}px`);
      target.style.setProperty('--bws-nav-cover-w', `${cover.width}px`);
      target.style.setProperty('--bws-nav-cover-h', `${cover.height}px`);
      target.style.setProperty('--bws-nav-cover-fade', `${fade}px`);
    }
  }
}

if (!customElements.get('bws-pdp-section-navigation')) {
  customElements.define('bws-pdp-section-navigation', BwsPdpSectionNavigation);
}
